import React from 'react';
import { TomeStar, TomeWaxSeal } from './TomeOrnaments';
import { TomeFrame, type TomeFramePiece, type TomeFramePreset } from './TomeFrame';
import { TomeSkin, type TomeShapeProps } from './TomeSkin';
import type { TomeTexture } from './tomeVibes';
import { useTomeVibe } from './tomeVibeContext';

/** A folded paper plaque with a star pin ("Featured quest"). Positioned by its parent. */
export const TomePlaque: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) =>
  <span className={`tome-plaque ${className}`}><TomeStar />{children}</span>;

/** A hanging silk bookmark at the bottom edge of a folio. Decorative. */
export const TomeBookmark: React.FC = () => <span className="tome-bookmark" aria-hidden="true" />;

export interface TomeFolioProps extends React.HTMLAttributes<HTMLElement>, TomeShapeProps {
  /** The plaque's words; no plaque when omitted. */
  plaque?: React.ReactNode;
  /** `open` presses the Coil spiral, `locked` a lock; no seal when omitted. */
  seal?: 'open' | 'locked';
  /** Hang a silk bookmark from the bottom edge. */
  bookmark?: boolean;
  /** Quieter vellum for something not yet open (a teaser). */
  muted?: boolean;
  /** Paper texture. Default `vellum`. Roughness defaults to the panel vibe's. */
  texture?: TomeTexture;
  /** Overhanging pieces. Default `vines` (top-left and bottom-right). */
  frame?: TomeFramePreset | TomeFramePiece[];
  children?: React.ReactNode;
}

/**
 * The strongest thing on a page: a brighter leaf with a deckled edge, chamfered
 * corners and a gilt double rule (shape props as on `TomePanel`), vine corners, a plaque, a wax seal and a bookmark. One per important item (a
 * featured quest); everything else on the page is a `<TomeEntry>`.
 */
export const TomeFolio: React.FC<TomeFolioProps> = ({
  plaque, seal, bookmark, muted, className = '', children,
  edge = 'deckled', corners = 'chamfer', roughness, shapeSeed, texture = 'vellum', frame = 'vines', ...rest
}) => {
  const vibe = useTomeVibe();
  return <article {...rest} className={`tome-folio ${className}`} data-muted={muted || undefined}>
    <TomeSkin layer="leaf" edge={edge} corners={corners} roughness={roughness ?? vibe.shape.roughness}
      shapeSeed={shapeSeed ?? rest['aria-label'] ?? 'folio'} texture={texture} rule="double" />
    <TomeFrame frame={frame} />
    {plaque && <TomePlaque>{plaque}</TomePlaque>}
    {seal && <TomeWaxSeal locked={seal === 'locked'} />}
    <div className="tome-folio-body">{children}</div>
    {bookmark && <TomeBookmark />}
  </article>;
};
